import z from 'zod';

/**
 * DefrostScheduleEvent
 *
 * Zod schema for a single defrost schedule event with start time, end date, and recurrence days.
 * Validates that all required fields are provided for proper defrost scheduling.
 */
const DefrostScheduleEvent = z.object({
  startTime: z.date({ required_error: 'Start time must be provided' }),
  endDate: z.date().optional().nullable(),
  eventDays: z.array(z.string(), { required_error: 'Event days must be provided' }).min(1, {
    message: 'Event days must be provided',
  }),
});

/**
 * SequencerItem
 *
 * Zod schema for a single sequencer item containing a name and stage values.
 * Ensures that at least one stage value is provided for the sequencer operation.
 */
const SequencerItem = z.object({
  name: z.string({ required_error: 'Name must be provided' }),
  stageValues: z.array(z.number(), { required_error: 'Stage values must be provided' }).min(1, {
    message: 'Stage values must be provided',
  }),
});

/**
 * SequencerTable
 *
 * Zod schema for a sequencer table containing an array of sequencer items.
 * Represents the complete configuration for a sequencer's stage-based operation.
 */
const SequencerTable = z.object({
  table: z.array(SequencerItem),
});

/**
 * DetailsFormSchema
 *
 * Complete Zod validation schema for the equipment details form.
 * Validates all form data including settings, sequencer configuration, and defrost schedules.
 * Used for form validation and type inference via DetailsFormData type.
 *
 * @property defrostSchedule - Array of defrost schedule events with timing and recurrence
 * @property settings - Record of equipment settings keyed by setting ID
 * @property sequencerSettings - Record of sequencer-specific settings keyed by setting ID
 * @property sequencer - Record of sequencer tables keyed by sequencer ID
 */
export const DetailsFormSchema = z.object({
  defrostSchedule: z.array(DefrostScheduleEvent),
  settings: z.record(z.string(), z.any({ required_error: 'Setting value must be provided' })),
  sequencerSettings: z.record(
    z.string(),
    z.any({ required_error: 'Sequencer setting value must be provided' })
  ),
  sequencer: z.record(z.string(), SequencerTable),
});
